import { redactedJson } from '../lib/redaction';
import type { PersonaListCopyKey } from './copy';

type Tone = 'green' | 'blue' | 'yellow' | 'red' | 'muted';

export type PersonaListSources = {
  personas?: unknown;
  personaConfigs?: unknown;
  error?: string;
};

export type CreatePersonaForm = {
  name: string;
  displayName: string;
  description: string;
  agentType: string;
  provider: string;
  model: string;
};

export type PersonaListRow = {
  name: string;
  displayName: string;
  description: string;
  status: string;
  agentType: string;
  platforms: string[];
  sessionsCount: number;
  liveSessionsCount: number;
  skillsCount: number;
  workDir: string;
  detailHref: string;
};

export type PersonaListModel = {
  copyKeys: {
    title: 'title';
    description: 'description';
    emptyTitle: 'emptyTitle';
    emptyDescription: 'emptyDescription';
    unauthorizedTitle: 'unauthorizedTitle';
    errorTitle: 'errorTitle';
  };
  rows: PersonaListRow[];
  status: Array<{ labelKey: PersonaListCopyKey; value: string; evidence: string; evidenceKey?: PersonaListCopyKey; tone: Tone }>;
  emptyState: { titleKey: PersonaListCopyKey; descriptionKey: PersonaListCopyKey } | null;
  partialNotices: string[];
  rawJson: string;
};

export function buildPersonaListModel(sources: PersonaListSources): PersonaListModel {
  const runtimeRows = personaRecords(sources.personas).map(personaRow).filter(row => row.name);
  const configRows = personaConfigRecords(sources.personaConfigs).map(personaConfigRow).filter(row => row.name);
  const rows = mergePersonaRows(runtimeRows, configRows);
  const platformCount = new Set(rows.flatMap(row => row.platforms)).size;
  const liveSessionsCount = rows.reduce((total, row) => total + row.liveSessionsCount, 0);
  const notices = sources.error ? [sources.error] : [];

  return {
    copyKeys: {
      title: 'title',
      description: 'description',
      emptyTitle: 'emptyTitle',
      emptyDescription: 'emptyDescription',
      unauthorizedTitle: 'unauthorizedTitle',
      errorTitle: 'errorTitle',
    },
    rows,
    status: [
      { labelKey: 'status.personas', value: String(rows.length), evidence: configRows.length ? '/api/v1/personas + /api/v1/persona_configs' : '/api/v1/personas', tone: rows.length ? 'green' : 'muted' },
      { labelKey: 'status.liveSessions', value: String(liveSessionsCount), evidence: 'live_sessions_count', tone: liveSessionsCount ? 'green' : 'muted' },
      { labelKey: 'status.platforms', value: String(platformCount), evidence: '', evidenceKey: 'status.distinctPlatforms', tone: platformCount ? 'blue' : 'muted' },
      { labelKey: 'status.partial', value: String(notices.length), evidence: '', evidenceKey: 'status.partialEvidence', tone: notices.length ? 'yellow' : 'green' },
    ],
    emptyState: rows.length ? null : {
      titleKey: 'emptyTitle',
      descriptionKey: 'emptyDescription',
    },
    partialNotices: notices,
    rawJson: redactedJson(sources),
  };
}

export function defaultCreatePersonaForm(): CreatePersonaForm {
  return {
    name: '',
    displayName: '',
    description: '',
    agentType: 'codex',
    provider: '',
    model: '',
  };
}

export function buildCreatePersonaConfigPayload(form: CreatePersonaForm) {
  return {
    project_name: form.name.trim(),
    display_name: form.displayName.trim(),
    description: form.description.trim(),
    actor: 'console',
  };
}

export function buildInitialPersonaDraft(form: CreatePersonaForm) {
  const provider = form.provider.trim();
  const model = form.model.trim();
  const providerSpec = provider ? [{ name: provider, ...(model ? { model } : {}) }] : [];
  return {
    agent: {
      type: form.agentType.trim() || 'codex',
      providers: providerSpec,
      ...(provider ? { provider } : {}),
      ...(model ? { model } : {}),
    },
    skills: [],
    prompt_modules: [],
    mcp_servers: [],
    platforms: [],
  };
}

function personaRow(value: Record<string, unknown>): PersonaListRow {
  const name = text(value.name);
  return {
    name,
    displayName: text(value.display_name),
    description: text(value.description),
    status: text(value.status) || 'active',
    agentType: text(value.agent_type),
    platforms: strings(value.platforms),
    sessionsCount: numberValue(value.sessions_count),
    liveSessionsCount: numberValue(value.live_sessions_count),
    skillsCount: numberValue(value.skills_count),
    workDir: text(value.work_dir),
    detailHref: `#/personas/${encodeURIComponent(name)}/workbench`,
  };
}

function personaConfigRow(value: Record<string, unknown>): PersonaListRow {
  const name = text(value.project_name) || text(value.name) || text(value.id);
  const agent = asRecord(value.agent);
  return {
    name,
    displayName: text(value.display_name),
    description: text(value.description),
    status: text(value.status) || 'active',
    agentType: text(value.agent_type) || text(agent.type),
    platforms: strings(value.platforms),
    sessionsCount: 0,
    liveSessionsCount: 0,
    skillsCount: numberValue(value.skills_count),
    workDir: text(value.work_dir),
    detailHref: `#/personas/${encodeURIComponent(name)}/workbench`,
  };
}

function mergePersonaRows(runtimeRows: PersonaListRow[], configRows: PersonaListRow[]) {
  const configByName = new Map(configRows.map(row => [row.name, row]));
  const rows = runtimeRows.map(row => {
    const configRow = configByName.get(row.name);
    if (!configRow) return row;
    return {
      ...row,
      displayName: configRow.displayName || row.displayName,
      description: configRow.description || row.description,
      status: configRow.status || row.status,
      detailHref: configRow.detailHref || row.detailHref,
    };
  });
  const seen = new Set(rows.map(row => row.name));
  for (const row of configRows) {
    if (seen.has(row.name)) continue;
    rows.push(row);
    seen.add(row.name);
  }
  return rows;
}

function personaRecords(value: unknown): Record<string, unknown>[] {
  const record = asRecord(value);
  const personas = getArray(record.personas);
  if (personas.length) return personas.map(asRecord);
  return getArray(record.projects).map(asRecord);
}

function personaConfigRecords(value: unknown): Record<string, unknown>[] {
  const record = asRecord(value);
  const personaConfigs = getArray(record.persona_configs);
  if (personaConfigs.length) return personaConfigs.map(asRecord);
  return getArray(record.project_configs).map(asRecord);
}

function strings(value: unknown): string[] {
  return getArray(value).map(item => text(item)).filter(Boolean);
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function text(value: unknown) {
  return typeof value === 'string' ? value : value === undefined || value === null ? '' : String(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
